import { EColor } from '@ucheba/utils/helpers/styles/variables'
import { IPropHeight } from '@ucheba/utils/helpers/components/prop.types'
import { EDataKeys } from '@ucheba/utils/types/dataKeys'
import { institutionsProgramsSlice } from './index'

export interface IInstitutionsProgramsState {
  [key: string]: ReturnType<typeof institutionsProgramsSlice.reducer>
}

export interface IInstitutionsPreparatory {
  items: IProgramPreparatory[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsHeight {
  items: IPropHeight[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsVocational {
  items: IProgramVocational[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsMaster {
  items: IProgramMaster[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsPostgraduate {
  items: IProgramPostgraduate[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsMBA {
  items: IProgramMBA[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsCourse {
  items: IProgramCourse[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IInstitutionsProgramsSchool {
  items: IProgramSchool[]
  total: number
  facets: IProgramsByTypeFacets
}

export interface IProgramsByTypeFacets {
  price?: {
    min: number
    max: number
  }
  tags?: IInstitutionsProgramTags[]
  programTypes?: IProgramType[]
  locations?: {
    name: string
    count: number
  }
  trainingFormTypes?: IProgramType[]
  durationTypes?: IDurationTypes[]
  formats?: {
    name: string
    count: number
  }[]
  academicHours?: {
    min: number
    max: number
  }
  examSubjects?: {
    count: number
    iconUrl: string
    name: string
    value: number
    slug?: string
  }[]
}

interface IDurationTypes {
  name: keyof typeof EDurationTypes
  count: number
}

export enum EDurationTypes {
  one_day = 'one_day',
  less_then_week = 'less_then_week',
  week_to_month = 'week_to_month',
  month_and_more = 'month_and_more',
}

export interface IInstitutionsPrograms {
  items: {
    tag: IInstitutionsProgramTags
    programs: IProgram[]
    total: number
  }[]
}

export interface IInstitutionsProgramTags {
  id: number
  name: string
  color: keyof typeof EColor
  iconUrl: string | null
  nameDative: string
  nameGenitive: string
  nameAdjective: string
  nameProfPlural: string
  items: IInstitutionsProgramTags[]
}

export interface IProgram {
  id: number
  name: string
  programType: {
    value: number
    name: string
  }
  description: string | null
  shortDescription: string | null
  priceFrom: number | null
  priceTo: number | null
  institution: IInstitution | null
  faculty: IFaculty | null
  feedbackStats: IFeedbackStats | null
  budgetPlacesCount?: number | null
  averageBySubjectExamScore?: number | null
  optionalSubjects?: IExamSubject[]
  requiredSubjects?: IExamSubject[]
  diplomaAvgScore?: number | null
  additionalExams?: IAdditionalExam[] | null
  averageBySubjectExamScoreFrom?: number
  averageBySubjectExamScoreTo?: number
  diplomaAvgScoreFrom?: number
  diplomaAvgScoreTo?: number
  probability: number
  formats?: {
    value: keyof typeof EFormats
    name: string
  }[]
  trainingForm?: {
    name: string
    price: number
    academicHours: number
    schedule: string
    nearestDate: {
      dateTo: string | null
      isMonthStartAt: boolean
      dateFrom: string | null
    }
    duration: number
    durationType: {
      value: number
      name: string
    }
    startType: IProgramStartType
  }
  totalTrainingForms?: number
  isFree?: boolean | null
}

interface IExamSubject {
  id: number
  minPoints: number | null
  subject: ISubject
}

export interface IProgramStartType {
  value: keyof typeof EProgramStartType
  name: string
}

export enum EProgramStartType {
  date = 'date', // Дата
  groupIsFilled = 'group_is_filled', // По мере набора
  anyTime = 'any_time', // В любое время
}

export interface IProgramHigher extends IProgramDefault {
  budgetPlacesCount: number | null
  optionalSubjects: ISubject[]
  requiredSubjects: ISubject[]
  additionalExams: IAdditionalExam[] | null
  averageBySubjectExamScoreFrom: number
  averageBySubjectExamScoreTo: number
}

export interface IProgramVocational extends IProgramDefault {
  diplomaAvgScoreFrom: number
  diplomaAvgScoreTo: number
  budgetPlacesCount: number | null
  additionalExams: IAdditionalExam[] | null
}

export interface IProgramMaster extends IProgramDefault {
  budgetPlacesCount: number | null
}

export interface IProgramPostgraduate extends IProgramDefault {
  budgetPlacesCount: number | null
}

export interface IProgramCourse extends IProgramDefault {
  formats: keyof (typeof EFormats)[]
  trainingForm: {
    name: string
    price: number
    academicHours: number
    schedule: string
    nearestDate: {
      dateTo: string | null
      isMonthStartAt: boolean
      dateFrom: string | null
    }
    duration: number
    durationType: number
    startType: string
  }
  totalTrainingForms: number
}

export interface IProgramPreparatory extends IProgramDefault {
  formats: keyof (typeof EFormats)[]
  trainingForm: {
    name: string
    price: number
    academicHours: number
    schedule: string
    nearestDate: {
      dateTo: string | null
      isMonthStartAt: boolean
      dateFrom: string | null
    }
    duration: number
    durationType: number
    startType: string
  }
  totalTrainingForms: number
}

export interface IProgramMBA extends IProgramDefault {
  formats: keyof (typeof EFormats)[]
  trainingForm: {
    name: string
    price: number
    academicHours: number
    schedule: string
    nearestDate: {
      dateTo: string | null
      isMonthStartAt: boolean
      dateFrom: string | null
    }
    duration: number
    durationType: number
    startType: string
  }
  totalTrainingForms: number
}

export interface IProgramSchool extends IProgramDefault {}

enum EFormats {
  offline = 'offline',
  online = 'online',
}

interface IProgramDefault {
  id: number
  name: string
  programType: number
  description: string | null
  shortDescription: string | null
  priceFrom: number | null
  priceTo: number | null
  institution: IInstitution | null
  faculty: IFaculty | null
}

interface IAdditionalExam {
  id: number
  title: string
  description: string
  subject: {
    id: number
    name: string
    slug: string | null
    iconUrl: string | null
  } | null
}

export interface IInstitution {
  id: number
  name: string
  type: IProgramType
  isState: boolean
  abbreviation: string | null
  description: string | null
  logo: {
    fallbackUrl: string
    webp: string | null
  } | null
  feedbackStats: IFeedbackStats | null
}

export interface IFaculty {
  id: number
  name: string
  type: IProgramType
  isState: boolean
  abbreviation: string
  description: string
  logoUrl: string
  feedbackStats: IFeedbackStats | null
}

export interface ISubject {
  id: number
  name: string
  slug: string | null
  iconUrl: string | null
  group: number | null
  hasInProfile: boolean
}

export interface IProgramType {
  value: number
  name: string
}

interface IFeedbackStats {
  total: number
  score: number | null
}

export enum ESlugsTypesProgram {
  higher = 'higher',
  vocational = 'vocational',
  master = 'master',
  postgraduate = 'postgraduate',
  course = 'course',
  mba = 'mba',
  school = 'school',
}

export enum ESortQueryKeys {
  price = 'price', // Стоимость
  date = 'date', // Дата старта
  duration = 'duration', // Длительность
  budgetPlaces = 'budget_places', // Количество бюджетных мест
  averageBySubjectExamScore = 'average_by_subject_exam_score', // Средний балл по предметам ЕГЭ
  diplomaAvgScore = 'diploma_avg_score', // Средний балл аттестата
  durationTypes = 'duration_value_types',
}

export enum ESortQueryValues {
  asc = 'asc',
  desc = 'desc',
}

export enum EQueryKeysByFacetName {
  tags = 'tagIds',
  programTypes = 'programTypes',
  locations = 'locationIds',
  examSubjects = 'examSubjectIds',
  trainingFormTypes = 'trainingFormTypes',
  formats = 'formats',
  sale = 'sale',
  withoutExams = 'hasAdditionalExam',
  durationTypes = 'durationValueTypes',
  specialityIds = 'specialityIds',
}

export enum EQueryKeysByPrice {
  priceFrom = EDataKeys.priceFrom,
  priceTo = EDataKeys.priceTo,
}

export enum EQueryKeysByAcademicHours {
  academicHoursFrom = EDataKeys.academicHoursFrom,
  academicHoursTo = EDataKeys.academicHoursTo,
}
